import { useCallback, useState } from 'react'

import { ApiError } from '@api'
import { zodResolver } from '@hookform/resolvers/zod'
import { useUnit } from 'effector-react'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router'
import * as z from 'zod'

import { Page } from '@/components/layout/page/page.tsx'
import { Button } from '@/components/ui/button.tsx'
import { FormField } from '@/components/ui/form/form-field.tsx'
import { $account } from '@/models/account.ts'
import { useAccount } from '@/modules/accounts/hooks/useAccounts'
import { DisableTfaDialog } from '@/modules/profile-settings/ui/tfa/disable-tfa-dialog.tsx'
import { EnableTfaDialog } from '@/modules/profile-settings/ui/tfa/enable-tfa-dialog.tsx'
import { AppRoutes } from '@/utils/constants'

import { useUpdateSettings } from '../hooks/useSettings'

type NewPasswordForm = z.infer<typeof newPasswordFormValues>

const INVALID_TFA_ERROR = 'Code is not correct'

const newPasswordFormValues = z
  .object({
    oldPassword: z
      .string()
      .min(8, { message: 'Password must be at least 8 characters' }),
    newPassword: z
      .string()
      .min(8, { message: 'Password must be at least 8 characters' }),
    newPasswordConfirm: z
      .string()
      .min(1, { message: 'Confirm Password is required' }),
    tfaCode: z.string().optional(),
    tfaRequired: z.boolean(),
  })
  .refine((data) => data.newPassword === data.newPasswordConfirm, {
    path: ['newPasswordConfirm'],
    message: "Passwords don't match",
  })
  .refine((data) => !data.tfaRequired || !!data.tfaCode, {
    path: ['tfaCode'],
    message: 'TFA code is required',
  })

export const SecuritySettingsTab = () => {
  const account = useUnit($account)
  const navigate = useNavigate()
  const { logout } = useAccount()
  const { updateProfileMutation } = useUpdateSettings()
  const form = useForm<NewPasswordForm>({
    mode: 'all',
    resolver: zodResolver(newPasswordFormValues),
    defaultValues: {
      tfaRequired: !!account?.tfaEnabled,
    },
  })
  const [showTfaEnableDialog, setShowTfaEnableDialog] = useState(false)
  const [showTfaDisableDialog, setShowTfaDisableDialog] = useState(false)

  const { handleSubmit } = form

  const onSubmit = useCallback(
    async ({ tfaRequired, ...values }: NewPasswordForm) => {
      try {
        await updateProfileMutation.mutateAsync(values)

        navigate(AppRoutes.Profile.Settings.asUrl())
      } catch (error) {
        const errorMessage = (error as ApiError)?.body?.detail

        if (!errorMessage) return

        if (errorMessage === INVALID_TFA_ERROR) {
          form.setError('tfaCode', {
            type: 'manual',
            message: errorMessage,
          })

          return
        }

        form.setError('oldPassword', {
          type: 'manual',
          message: errorMessage,
        })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateProfileMutation],
  )

  const handleCancel = useCallback(() => {
    form.reset({ oldPassword: '', newPassword: '', newPasswordConfirm: '' })
    form.clearErrors(['newPassword', 'newPasswordConfirm', 'oldPassword'])
  }, [form])

  return (
    <Page
      title="Security"
      description="Manage the security of your CreatorHero Account"
    >
      <div className="border-t pb-8" />
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid max-w-md gap-8">
            <div
              className="text-title text-xl font-semibold"
              data-testid="security-settings-tab-old-password-text"
            >
              Change Password
            </div>
            <FormField
              name="oldPassword"
              label="Current Password"
              placeholder="Current Password"
              type="password"
              isPassword
              testId="security-settings-tab-old-password"
            />

            <FormField
              name="newPassword"
              label="New Password"
              placeholder="New Password must be at least 8 characters"
              type="password"
              isPassword
              testId="security-settings-tab-new-password"
            />

            <FormField
              name="newPasswordConfirm"
              label="Confirm Password"
              placeholder="Confirm Password"
              type="password"
              isPassword
              testId="security-settings-tab-confirm-password"
            />

            {account?.tfaEnabled && (
              <FormField
                name="tfaCode"
                label="Two-factor Authentication Code"
                placeholder="TFA Code"
              />
            )}

            <div className="grid grid-flow-col gap-4">
              <Button
                onClick={handleCancel}
                type="reset"
                variant="outline"
                testId="security-settings-tab-cancel-button"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!form.formState.isDirty || !form.formState.isValid}
                testId="security-settings-tab-save-button"
              >
                Save
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
      <div className="mt-8 border-t pb-8" />
      <div className="flex items-center justify-between pb-8">
        <div className="flex flex-col">
          <h5 className="text-title font-medium">Two-Factor Authentication</h5>
          <p className="text-secondary-300 pt-1 text-sm font-medium">
            This allows you to add an extra layer of security using
            authenticator apps like Google Authenticator or Authy.
          </p>
        </div>
        {account?.tfaEnabled ? (
          <Button
            variant="destructive"
            onClick={() => setShowTfaDisableDialog(true)}
          >
            Deactivate
          </Button>
        ) : (
          <Button onClick={() => setShowTfaEnableDialog(true)}>Activate</Button>
        )}
      </div>
      {showTfaEnableDialog && (
        <EnableTfaDialog
          show={true}
          onClose={() => setShowTfaEnableDialog(false)}
        />
      )}
      {showTfaDisableDialog && (
        <DisableTfaDialog
          show={true}
          onSuccess={logout}
          onClose={() => setShowTfaDisableDialog(false)}
        />
      )}
    </Page>
  )
}
